// MPEG-TS demuxer for the return feed (flow 2): JBS rift_egress output,
// which uses its own PIDs (video 0x1011, audio 0x1101 from libs/mpegts_transport)
// — so PIDs are discovered from PAT → PMT, never assumed.
//
// Frame-aligned input: rift delivers one TS blob per encoded frame
// (`sendFrame`), and video PES are unbounded (PES_packet_length = 0), whose
// end is otherwise only known when the next PES starts. Calling `flush()`
// after each delivered payload emits that frame immediately instead of one
// frame late — a frame of latency the conversational loop does not spend.
import { PID_NULL, PID_PAT, STREAM_TYPE_AAC_ADTS, STREAM_TYPE_H264, STREAM_TYPE_HEVC, SYNC_BYTE, TS_PACKET_LEN, } from './constants.js';
function readTimestamp(b, off) {
    return (((b[off] >> 1) & 0x07) * 2 ** 30 +
        ((b[off + 1] << 22) | ((b[off + 2] >> 1) << 15) | (b[off + 3] << 7) | (b[off + 4] >> 1)));
}
export class TsDemuxer {
    pmtPid = PID_NULL;
    videoPid = PID_NULL;
    audioPid = PID_NULL;
    videoCodec = 'h264';
    pes = new Map();
    residual = null;
    /** Counters for the stats overlay / telemetry. */
    stats = { packets: 0, resyncs: 0, pesDropped: 0 };
    get pids() {
        return { pmt: this.pmtPid, video: this.videoPid, audio: this.audioPid };
    }
    /** Feed TS bytes; returns the frames completed by them. */
    push(bytes) {
        const out = [];
        let buf = bytes;
        if (this.residual) {
            const joined = new Uint8Array(this.residual.length + bytes.length);
            joined.set(this.residual, 0);
            joined.set(bytes, this.residual.length);
            buf = joined;
            this.residual = null;
        }
        let off = 0;
        while (off + TS_PACKET_LEN <= buf.length) {
            if (buf[off] !== SYNC_BYTE) {
                this.stats.resyncs++;
                const next = buf.indexOf(SYNC_BYTE, off + 1);
                if (next < 0) {
                    off = buf.length;
                    break;
                }
                off = next;
                continue;
            }
            this.packet(buf.subarray(off, off + TS_PACKET_LEN), out);
            off += TS_PACKET_LEN;
        }
        if (off < buf.length)
            this.residual = buf.slice(off);
        return out;
    }
    /** Emit every PES in progress (end of a frame-aligned payload). */
    flush() {
        const out = [];
        for (const pid of [...this.pes.keys()])
            this.finishPes(pid, out);
        return out;
    }
    reset() {
        this.pmtPid = this.videoPid = this.audioPid = PID_NULL;
        this.pes.clear();
        this.residual = null;
    }
    packet(p, out) {
        this.stats.packets++;
        if (p[1] & 0x80)
            return; // transport_error_indicator
        const pusi = (p[1] & 0x40) !== 0;
        const pid = ((p[1] & 0x1f) << 8) | p[2];
        const afc = (p[3] >> 4) & 0x03;
        let off = 4;
        let randomAccess = false;
        if (afc & 0x02) {
            const afLen = p[4];
            if (afLen > 0)
                randomAccess = (p[5] & 0x40) !== 0;
            off = 5 + afLen;
        }
        if (!(afc & 0x01) || off >= TS_PACKET_LEN)
            return;
        const payload = p.subarray(off);
        if (pid === PID_PAT) {
            if (pusi)
                this.parsePat(payload);
        }
        else if (pid === this.pmtPid) {
            if (pusi)
                this.parsePmt(payload);
        }
        else if (pid === this.videoPid || pid === this.audioPid) {
            if (pusi) {
                this.finishPes(pid, out);
                this.pes.set(pid, { chunks: [payload.slice()], size: payload.length, randomAccess });
            }
            else {
                const a = this.pes.get(pid);
                if (!a)
                    return; // joined mid-PES
                a.chunks.push(payload.slice());
                a.size += payload.length;
            }
            this.maybeFinishBounded(pid, out);
        }
    }
    section(payload) {
        const pointer = payload[0];
        const s = payload.subarray(1 + pointer);
        if (s.length < 3)
            return null;
        const len = ((s[1] & 0x0f) << 8) | s[2];
        if (3 + len > s.length)
            return null; // multi-packet sections: not produced by either muxer
        return s.subarray(0, 3 + len);
    }
    parsePat(payload) {
        const s = this.section(payload);
        if (!s || s[0] !== 0x00)
            return;
        for (let i = 8; i + 4 <= s.length - 4; i += 4) {
            const program = (s[i] << 8) | s[i + 1];
            if (program !== 0) {
                this.pmtPid = ((s[i + 2] & 0x1f) << 8) | s[i + 3];
                return;
            }
        }
    }
    parsePmt(payload) {
        const s = this.section(payload);
        if (!s || s[0] !== 0x02)
            return;
        const progInfoLen = ((s[10] & 0x0f) << 8) | s[11];
        let i = 12 + progInfoLen;
        const end = s.length - 4;
        let video = PID_NULL;
        let audio = PID_NULL;
        while (i + 5 <= end) {
            const type = s[i];
            const pid = ((s[i + 1] & 0x1f) << 8) | s[i + 2];
            const esInfoLen = ((s[i + 3] & 0x0f) << 8) | s[i + 4];
            if (video === PID_NULL && (type === STREAM_TYPE_H264 || type === STREAM_TYPE_HEVC)) {
                video = pid;
                this.videoCodec = type === STREAM_TYPE_HEVC ? 'hevc' : 'h264';
            }
            else if (audio === PID_NULL && type === STREAM_TYPE_AAC_ADTS) {
                audio = pid;
            }
            i += 5 + esInfoLen;
        }
        this.videoPid = video;
        this.audioPid = audio;
    }
    maybeFinishBounded(pid, out) {
        const a = this.pes.get(pid);
        if (!a || a.size < 6)
            return;
        const head = a.chunks[0].length >= 6 ? a.chunks[0] : this.joined(a);
        const pesLen = (head[4] << 8) | head[5];
        if (pesLen > 0 && a.size >= 6 + pesLen)
            this.finishPes(pid, out);
    }
    joined(a) {
        if (a.chunks.length === 1)
            return a.chunks[0];
        const buf = new Uint8Array(a.size);
        let off = 0;
        for (const c of a.chunks) {
            buf.set(c, off);
            off += c.length;
        }
        a.chunks = [buf];
        return buf;
    }
    finishPes(pid, out) {
        const a = this.pes.get(pid);
        if (!a)
            return;
        this.pes.delete(pid);
        const pes = this.joined(a);
        if (pes.length < 9 || pes[0] !== 0 || pes[1] !== 0 || pes[2] !== 1) {
            this.stats.pesDropped++;
            return;
        }
        const pesLen = (pes[4] << 8) | pes[5];
        const flags = pes[7];
        const hdrLen = pes[8];
        const dataStart = 9 + hdrLen;
        const dataEnd = pesLen > 0 ? Math.min(pes.length, 6 + pesLen) : pes.length;
        if (dataStart > dataEnd || !(flags & 0x80)) {
            this.stats.pesDropped++;
            return;
        }
        const pts = readTimestamp(pes, 9);
        const dts = (flags & 0x40) !== 0 ? readTimestamp(pes, 14) : pts;
        const data = pes.subarray(dataStart, dataEnd);
        if (pid === this.videoPid) {
            out.push({ kind: 'video', codec: this.videoCodec, pts90k: pts, dts90k: dts, randomAccess: a.randomAccess, data });
        }
        else if (pid === this.audioPid) {
            out.push({ kind: 'audio', pts90k: pts, data });
        }
    }
}
//# sourceMappingURL=demuxer.js.map